import { computed, inject, Injectable, Signal } from '@angular/core';
import { UserProfileUpdate } from '@features/user/types/user.types';
import { ResponseUserProfileDto } from '@kerberos-compliance/compliance-shared-ts/features/user/dtos/response-user-profile.dto';
import { AmlDeskFeatureTable } from '@kerberos-compliance/compliance-shared-ts/features/user/enums/feature-table.enum';
import {
  ColumnPreference,
  TablePreference,
  UserProfilePreferences,
  UserTablePreferences,
} from '@kerberos-compliance/compliance-shared-ts/features/user/types/user-profile.types';
import { TablePreferencesProvider, UserProfileStore } from '@kerberos-compliance/compliance-shared-ui';
import { UserProfileService } from '@/user/services/user-profile.service';

@Injectable()
export class AmlTablePreferenceProviderService extends TablePreferencesProvider<AmlDeskFeatureTable> {
  private store = inject(UserProfileStore);

  private userProfileService = inject(UserProfileService);

  /**
   * Returns a reactive signal containing the AML Desk platform's table preferences slice.
   *
   * @returns {Signal<Record<string, TablePreference>>} A signal with the table preferences or an empty object.
   */
  public getPlatformSlice(): Signal<Record<string, TablePreference>> {
    return computed(() => this.store.allTablePreferences().amlDesk ?? {});
  }

  /**
   * Persists the user preferences to the backend via HTTP PATCH.
   *
   * @param {UserProfilePreferences} userPreferences - The complete user preferences object to save.
   * @returns {Promise<ResponseUserProfileDto>} A promise that resolves to the updated user profile.
   */
  public async savePreferencesToBackend(userPreferences: UserProfilePreferences): Promise<ResponseUserProfileDto> {
    return this.userProfileService.updateUserProfile({
      userPreferences,
    } satisfies UserProfileUpdate);
  }

  /**
   * Merges new column preferences for a specific table into the existing preferences.
   *
   * @param {AmlDeskFeatureTable} tableKey - The unique identifier for the table being updated.
   * @param {ColumnPreference[]} [newPreferences] - The new column preferences to save, or undefined to remove.
   * @returns {UserTablePreferences} The merged user table preferences object.
   */
  public mergeWithExistingPreferences(
    tableKey: AmlDeskFeatureTable,
    newPreferences?: ColumnPreference[],
  ): UserTablePreferences {
    const allTablePreferences = this.store.allTablePreferences();
    const tablePreferences = { ...allTablePreferences.amlDesk };

    let newTablePreferences: Record<string, TablePreference>;

    if (newPreferences && newPreferences.length > 0) {
      newTablePreferences = {
        ...tablePreferences,
        [tableKey]: {
          ...tablePreferences[tableKey],
          columns: newPreferences,
        },
      };
    } else {
      // eslint-disable-next-line sonarjs/no-unused-vars -- rest destructuring drops the table key
      const { [tableKey]: removed, ...remainingTables } = tablePreferences;
      newTablePreferences = remainingTables;
    }

    return {
      ...allTablePreferences,
      amlDesk: newTablePreferences,
    } satisfies UserTablePreferences;
  }
}
